/**
 * The catalog — schemas, types (and core's users) — at boot, and kept current.
 *
 * @auger/core's catalog holds it and knows its version (`last_updated` and
 * `schema_count`). This keeps a copy in IndexedDB, so a returning visit renders
 * from it without waiting on the network: the copy registers at once and is
 * checked in the background, reloading only if the database moved. A first
 * visit (no copy), or a copy that cannot be registered, loads and waits.
 *
 * While the app is open, the catalog is checked again whenever the tab comes
 * back into view or regains focus, and every minute while it is visible — at
 * most every 10s (core's refresh) — so a schema edited elsewhere (another tab,
 * another person, the MCP) arrives without a reload. Writes made here update
 * the catalog in place. Checks that keep failing say so once, in a toast: the
 * app is then showing a copy it cannot confirm.
 */
import { toast } from "vue-sonner"
import { getAuger } from "@/init/auger"
import { clearCatalog, readCatalog, writeCatalog } from "@/init/catalogStorage"

export const RECHECK_EVERY_MS = 60_000
export const FAILURES_BEFORE_WARNING = 3

let failures = 0

function recheck(): void {
  getAuger()
    .catalog.refresh()
    .then(
      () => {
        failures = 0
      },
      (err) => {
        failures++
        console.warn("[catalog] refresh failed", err)
        if (failures === FAILURES_BEFORE_WARNING) {
          toast.warning(
            "Can't reach Auger to check for schema changes — fields may be out of date."
          )
        }
      }
    )
}

let started: Promise<void> | null = null
let persisting = false
let listening = false

/**
 * Have a catalog registered: a persisted copy at once (checked in the
 * background), or a fresh load. Idempotent — the router guard calls it on
 * every navigation.
 */
export function startCatalog(): Promise<void> {
  started ??= (async () => {
    const catalog = getAuger().catalog
    if (!persisting) {
      persisting = true
      catalog.onChange(() => {
        const snapshot = catalog.snapshot()
        if (snapshot) void writeCatalog(snapshot)
      })
    }

    const persisted = await readCatalog()
    let registered = false
    if (persisted) {
      try {
        catalog.register(persisted)
        registered = true
      } catch (err) {
        // A copy this build cannot read (a shape change with no FORMAT bump).
        // Drop it, or every boot would trip on it again.
        console.warn("[catalog] the persisted catalog could not be registered", err)
        void clearCatalog()
      }
    }
    if (registered) recheck()
    // Forced: a copy that failed half-registered must not be checked as current.
    else await catalog.refresh({ force: true })

    if (!listening) {
      listening = true
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") recheck()
      })
      window.addEventListener("focus", recheck)
      setInterval(() => {
        if (document.visibilityState === "visible") recheck()
      }, RECHECK_EVERY_MS)
    }
  })().catch((err) => {
    // Let the next navigation try again rather than replaying the failure.
    started = null
    throw err
  })
  return started
}
